import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Select, Input,
  Space, Descriptions, message, Spin, Radio, Image
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined,
  EyeOutlined, EditOutlined, ReloadOutlined,
  StarOutlined
} from '@ant-design/icons';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  getDealerProducts, getDealerProductStats,
  approveProduct, rejectProduct,
  changeTier, toggleFeatured
} from '../../services/dealerService';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;

const TIER_CONFIG = {
  economy:      { label: 'Economy',      color: '#52C41A', bg: 'rgba(82,196,26,0.12)',    commission: '2%' },
  premium:      { label: 'Premium',      color: '#1890FF', bg: 'rgba(24,144,255,0.12)',   commission: '3%' },
  luxury:       { label: 'Luxury',       color: '#C9A84C', bg: 'rgba(201,168,76,0.12)',   commission: '4%' },
  super_luxury: { label: 'Super Luxury', color: '#722ED1', bg: 'rgba(114,46,209,0.12)',   commission: '5%' },
};

const STATUS_CONFIG = {
  pending_review: { color: '#FAAD14', label: 'Pending Review', badge: 'warning' },
  active:         { color: '#52C41A', label: 'Active',         badge: 'success' },
  rejected:       { color: '#FF4D4F', label: 'Rejected',       badge: 'error'   },
  inactive:       { color: '#7A8A96', label: 'Inactive',       badge: 'default' },
};

export default function DealerProductApproval() {
  const [products, setProducts]       = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [selected, setSelected]       = useState(null);
  const [stats, setStats]             = useState({});
  const [viewModal, setViewModal]     = useState(false);
  const [tierModal, setTierModal]     = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [actionType, setActionType]   = useState('');
  const [newTier, setNewTier]         = useState('');
  const [tierReason, setTierReason]   = useState('');
  const [reason, setReason]           = useState('');
  const [filterStatus, setFilterStatus]     = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading]         = useState(true);
  const [actionLoading, setActionLoading]   = useState(false);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    let result = products;
    if (filterStatus !== 'all')   result = result.filter(p => p.status === filterStatus);
    if (filterCategory !== 'all') result = result.filter(p => p.category === filterCategory);
    setFiltered(result);
  }, [filterStatus, filterCategory, products]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productList, productStats] = await Promise.all([
        getDealerProducts(),
        getDealerProductStats(),
      ]);
      setProducts(productList);
      setFiltered(productList);
      setStats(productStats);
    } catch {
      message.error('Failed to load dealer products');
    }
    setLoading(false);
  };

  const handleAction = async () => {
    if (actionType === 'rejected' && !reason) {
      message.warning('Please provide rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      const result = actionType === 'active'
        ? await approveProduct(selected.id, selected.tier || 'economy', 'nksapcolouracademy@gmail.com')
        : await rejectProduct(selected.id, reason, 'nksapcolouracademy@gmail.com');

      if (result?.success) {
        await addDoc(collection(db, 'auditLogs'), {
          action: 'DEALER PRODUCT ' + actionType.toUpperCase(),
          details: `Product: ${selected.name} | Dealer: ${selected.dealerName}${reason ? ' | Reason: ' + reason : ''}`,
          adminEmail: 'nksapcolouracademy@gmail.com',
          targetId: selected.id, targetType: 'dealer_product',
          timestamp: serverTimestamp(),
        });
        message.success(`Product ${actionType === 'active' ? 'approved' : 'rejected'} — saved to Firebase`);
        setActionModal(false);
        setViewModal(false);
        setReason('');
        loadData();
      }
    } catch {
      message.error('Action failed');
    }
    setActionLoading(false);
  };

  const handleTierChange = async () => {
    if (!newTier) { message.warning('Please select a tier'); return; }
    setActionLoading(true);
    try {
      const result = await changeTier(selected.id, newTier, tierReason, 'nksapcolouracademy@gmail.com');
      if (result?.success) {
        await addDoc(collection(db, 'auditLogs'), {
          action: 'DEALER PRODUCT TIER CHANGED',
          details: `Product: ${selected.name} | From: ${selected.tier} | To: ${newTier}${tierReason ? ' | Reason: ' + tierReason : ''}`,
          adminEmail: 'nksapcolouracademy@gmail.com',
          targetId: selected.id, targetType: 'dealer_product',
          timestamp: serverTimestamp(),
        });
        message.success(`Tier updated to ${TIER_CONFIG[newTier].label}`);
        setTierModal(false);
        setNewTier('');
        setTierReason('');
        loadData();
      }
    } catch {
      message.error('Failed to update tier');
    }
    setActionLoading(false);
  };

  const handleToggleFeatured = async (productId, current) => {
    try {
      await toggleFeatured(productId, !current, 'nksapcolouracademy@gmail.com');
      message.success(current ? 'Removed from featured' : 'Marked as featured');
      loadData();
    } catch {
      message.error('Failed to update featured status');
    }
  };

  const formatDate = (ts) => {
    if (!ts?.seconds) return 'N/A';
    return new Date(ts.seconds * 1000).toLocaleDateString('en-IN');
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  const columns = [
    {
      title: 'Product',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {r.images?.[0] ? (
            <img src={r.images[0]} alt=""
              style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
              onError={e => e.target.style.display = 'none'}
            />
          ) : (
            <div style={{
              width: 40, height: 40, borderRadius: 6, flexShrink: 0,
              background: 'rgba(201,168,76,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#C9A84C', fontSize: 18
            }}>📦</div>
          )}
          <div>
            <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#7A8A96' }}>
              {r.dealerName} • {r.dealerCity}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (c) => <span style={{ color: '#B0B8C1', fontSize: 12 }}>{c}</span>,
    },
    {
      title: 'Price',
      render: (_, r) => (
        <div>
          <div style={{ color: '#C9A84C', fontWeight: 500 }}>
            ₹{Number(r.sellingPrice || r.price || 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>per {r.unit}</div>
        </div>
      ),
    },
    {
      title: 'Tier',
      render: (_, r) => {
        const tier = TIER_CONFIG[r.tier];
        if (!tier) return <span style={{ color: '#7A8A96', fontSize: 12 }}>Not assigned</span>;
        return (
          <Tag style={{
            background: tier.bg, color: tier.color,
            border: '0.5px solid ' + tier.color + '50',
            borderRadius: 20, fontSize: 11
          }}>
            {tier.label}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending_review;
        return (
          <Badge status={cfg.badge}
            text={<span style={{ color: cfg.color, fontSize: 12 }}>{cfg.label}</span>} />
        );
      },
    },
    {
      title: 'Featured',
      render: (_, r) => (
        <Button
          type="text"
          icon={<StarOutlined />}
          style={{ color: r.isFeatured ? '#C9A84C' : '#4A4A47' }}
          onClick={() => handleToggleFeatured(r.id, r.isFeatured)}
        />
      ),
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelected(r); setViewModal(true); }} />
          <Button type="text" icon={<EditOutlined />} style={{ color: '#B0B8C1' }}
            onClick={() => { setSelected(r); setNewTier(r.tier || ''); setTierModal(true); }} />
          {r.status === 'pending_review' && (
            <>
              <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
                onClick={() => { setSelected(r); setActionType('active'); setActionModal(true); }} />
              <Button type="text" icon={<CloseCircleOutlined />} style={{ color: '#FF4D4F' }}
                onClick={() => { setSelected(r); setActionType('rejected'); setActionModal(true); }} />
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Products',  value: stats.total          || 0, color: '#C9A84C' },
          { title: 'Pending Review',  value: stats.pending_review || 0, color: '#FAAD14' },
          { title: 'Active Products', value: stats.active         || 0, color: '#52C41A' },
          { title: 'Rejected',        value: stats.rejected       || 0, color: '#FF4D4F' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic
                title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value}
                valueStyle={{ color: stat.color, fontSize: 28, fontWeight: 600 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 160 }}>
            <Option value="all">All Status</Option>
            <Option value="pending_review">Pending Review</Option>
            <Option value="active">Active</Option>
            <Option value="rejected">Rejected</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
          <Select value={filterCategory} onChange={setFilterCategory} style={{ width: 200 }}>
            <Option value="all">All Categories</Option>
            {categories.map(cat => <Option key={cat} value={cat}>{cat}</Option>)}
          </Select>
          <Button type="text" icon={<ReloadOutlined />}
            style={{ color: '#C9A84C' }} onClick={loadData}>
            Refresh
          </Button>
          <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>
            {loading ? 'Loading...' : `${filtered.length} of ${products.length} products`}
          </span>
        </div>
      </Card>

      {/* Table */}
      <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Spin>
      </Card>

      {/* View Modal */}
      <Modal
        title={<span style={{ color: '#C9A84C' }}>Product Details — {selected?.name}</span>}
        open={viewModal}
        onCancel={() => setViewModal(false)}
        width={700}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setViewModal(false)}>Close</Button>
            <Button icon={<EditOutlined />} style={{ color: '#B0B8C1' }}
              onClick={() => { setNewTier(selected?.tier || ''); setTierModal(true); }}>
              Change Tier
            </Button>
            {selected?.status === 'pending_review' && (
              <>
                <Button danger icon={<CloseCircleOutlined />}
                  onClick={() => { setActionType('rejected'); setActionModal(true); }}>
                  Reject
                </Button>
                <Button icon={<CheckCircleOutlined />}
                  style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                  onClick={() => { setActionType('active'); setActionModal(true); }}>
                  Approve
                </Button>
              </>
            )}
          </div>
        }
      >
        {selected && (
          <div>
            {/* Product images */}
            {selected.images?.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {selected.images.map((img, i) => (
                  <Image key={i} src={img} width={120} height={90}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                  />
                ))}
              </div>
            )}
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Product Name" span={2}>{selected.name}</Descriptions.Item>
              <Descriptions.Item label="Dealer">{selected.dealerName}</Descriptions.Item>
              <Descriptions.Item label="City">{selected.dealerCity}</Descriptions.Item>
              <Descriptions.Item label="Category" span={2}>{selected.category}</Descriptions.Item>
              <Descriptions.Item label="Brand">{selected.brand || '—'}</Descriptions.Item>
              <Descriptions.Item label="Selling Price">
                ₹{Number(selected.sellingPrice || 0).toLocaleString()} per {selected.unit}
              </Descriptions.Item>
              {selected.mrp > 0 && (
                <Descriptions.Item label="MRP">₹{Number(selected.mrp).toLocaleString()}</Descriptions.Item>
              )}
              <Descriptions.Item label="Stock">{selected.stock} {selected.unit}</Descriptions.Item>
              <Descriptions.Item label="Price Updates">{selected.priceUpdateFrequency}</Descriptions.Item>
              <Descriptions.Item label="Assigned Tier">
                {selected.tier ? (
                  <Tag style={{
                    background: TIER_CONFIG[selected.tier]?.bg,
                    color: TIER_CONFIG[selected.tier]?.color,
                    border: 'none', borderRadius: 20
                  }}>
                    {TIER_CONFIG[selected.tier]?.label} — {TIER_CONFIG[selected.tier]?.commission} commission
                  </Tag>
                ) : <span style={{ color: '#7A8A96' }}>Not assigned yet</span>}
              </Descriptions.Item>
              <Descriptions.Item label="Featured">
                {selected.isFeatured ? '⭐ Yes' : 'No'}
              </Descriptions.Item>
              {selected.description && (
                <Descriptions.Item label="Description" span={2}>
                  {selected.description}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Submitted">{formatDate(selected.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  status={(STATUS_CONFIG[selected.status] || STATUS_CONFIG.pending_review).badge}
                  text={selected.status}
                />
              </Descriptions.Item>
              {selected.rejectionReason && (
                <Descriptions.Item label="Rejection Reason" span={2}>
                  <span style={{ color: '#FF4D4F' }}>{selected.rejectionReason}</span>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Tier Modal */}
      <Modal
        title={<span style={{ color: '#C9A84C' }}>Assign Tier — {selected?.name}</span>}
        open={tierModal}
        onCancel={() => { setTierModal(false); setNewTier(''); setTierReason(''); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setTierModal(false); setNewTier(''); setTierReason(''); }}>
              Cancel
            </Button>
            <Button loading={actionLoading} onClick={handleTierChange}
              style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}>
              Update Tier
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Radio.Group
            value={newTier}
            onChange={e => setNewTier(e.target.value)}
            style={{ width: '100%' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(TIER_CONFIG).map(([key, val]) => (
                <Radio key={key} value={key}>
                  <Tag style={{
                    background: val.bg, color: val.color,
                    border: '0.5px solid ' + val.color + '50',
                    borderRadius: 20, marginLeft: 8
                  }}>
                    {val.label}
                  </Tag>
                  <span style={{ color: '#7A8A96', fontSize: 12 }}>
                    — {val.commission} commission
                    {key === 'economy'      && ' · Budget, local brands'}
                    {key === 'premium'      && ' · Trusted national brands'}
                    {key === 'luxury'       && ' · High-end, premium quality'}
                    {key === 'super_luxury' && ' · Imported, exclusive'}
                  </span>
                </Radio>
              ))}
            </div>
          </Radio.Group>
        </div>
        <Input.TextArea
          rows={2}
          placeholder="Optional: Reason for tier assignment..."
          value={tierReason}
          onChange={e => setTierReason(e.target.value)}
          style={{
            background: '#222222',
            border: '0.5px solid rgba(201,168,76,0.3)',
            color: '#D4DCE3'
          }}
        />
      </Modal>

      {/* Approve/Reject Modal */}
      <Modal
        title={
          <span style={{ color: actionType === 'active' ? '#52C41A' : '#FF4D4F' }}>
            {actionType === 'active' ? 'Approve Product' : 'Reject Product'} — {selected?.name}
          </span>
        }
        open={actionModal}
        onCancel={() => { setActionModal(false); setReason(''); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setActionModal(false); setReason(''); }}>Cancel</Button>
            <Button
              loading={actionLoading}
              danger={actionType === 'rejected'}
              style={actionType === 'active'
                ? { background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }
                : {}}
              onClick={handleAction}
            >
              Confirm {actionType === 'active' ? 'Approval' : 'Rejection'}
            </Button>
          </div>
        }
      >
        <div style={{ color: '#B0B8C1', fontSize: 13, marginBottom: 16 }}>
          {actionType === 'active'
            ? `Product will go live on HomeOwner app under ${TIER_CONFIG[selected?.tier]?.label || 'Economy'} tier.`
            : 'Dealer will be notified to fix and resubmit the product.'}
        </div>
        {actionType === 'rejected' && (
          <Input.TextArea
            rows={3}
            placeholder="Required: Reason for rejection..."
            value={reason}
            onChange={e => setReason(e.target.value)}
            style={{
              background: '#222222',
              border: '0.5px solid rgba(201,168,76,0.3)',
              color: '#D4DCE3'
            }}
          />
        )}
      </Modal>
    </div>
  );
}